import { useEffect, useRef } from 'react';
import { POLLING_INTERVALS } from '../../constants/timing.constants';

export interface UsePollingOptions {
  /**
   * Callback function to execute on each poll
   * Can be synchronous or asynchronous
   */
  callback: () => void | Promise<void>;

  /**
   * Interval in milliseconds between polls
   */
  interval?: number;

  /**
   * Whether polling should be enabled
   * When false, the interval is cleared and callback is not executed
   * @default true
   */
  enabled?: boolean;

  /**
   * Whether to run callback immediately on mount
   * @default true
   */
  runOnMount?: boolean;
}

/**
 * Generic polling hook that executes a callback at regular intervals
 *
 * Features:
 * - Proper cleanup to prevent memory leaks
 * - Uses ref pattern to avoid stale closures
 * - Can be enabled/disabled dynamically
 * - Configurable initial execution
 *
 * @example
 * ```typescript
 * // Poll API every 30 seconds
 * usePolling({
 *   callback: fetchData,
 *   interval: 30000,
 *   enabled: true
 * });
 *
 * // Poll with different interval, don't run on mount
 * usePolling({
 *   callback: checkStatus,
 *   interval: 5000,
 *   enabled: isActive,
 *   runOnMount: false
 * });
 * ```
 */
export const usePolling = ({
  callback,
  interval = POLLING_INTERVALS.DEFAULT,
  enabled = true,
  runOnMount = true,
}: UsePollingOptions): void => {
  // Use ref to avoid stale closure issues
  // This ensures the latest callback is always executed
  const callbackRef = useRef(callback);

  // Update ref when callback changes
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  useEffect(() => {
    // Don't set up polling if disabled
    if (!enabled) {
      return;
    }

    // Execute immediately if requested
    if (runOnMount) {
      callbackRef.current();
    }

    // Set up interval for recurring execution
    const intervalId = setInterval(() => {
      callbackRef.current();
    }, interval);

    // Cleanup: clear interval on unmount or when dependencies change
    // This prevents memory leaks and ensures old intervals don't persist
    return () => {
      clearInterval(intervalId);
    };
  }, [interval, enabled, runOnMount]);
};
